import { useCartStore } from '@/stores/cart'
import { useCatalogStore } from '@/stores/catalog'

export interface RewardCrossingGuard {
  /** The current effective subtotal — capture this *before* a cart mutation. */
  captureSubtotal: () => number
  /**
   * Whether to skip the generic green add/remove cart toast for a mutation, so it never collides
   * with the gradient reward toast. True only when the mutation crossed a reward tier — i.e. when
   * `useRewardThresholdCrossing` fires a reward toast for the same mutation and we don't want both
   * (BugHerd #108). When no tier is crossed the green toast still shows, on every viewport.
   *
   * Capture the subtotal before the mutation, then ask this after it. Returns false on legacy-bar
   * catalogs (which fire no reward toast), so the generic toast is never wrongly suppressed there.
   */
  shouldSuppressGenericCartToast: (beforeSubtotal: number) => boolean
}

/**
 * Single source of truth for whether a cart-operation handler should suppress its generic add/remove
 * toast in favour of the reward toast. Used by every green-cart-toast site (`bundleListItem`,
 * `PackageBuilderFooter`, `QuickAddsFlyout`, `ConfigureView`) so the suppression rule stays consistent.
 */
export function useRewardCrossingGuard(): RewardCrossingGuard {
  const cartStore = useCartStore()
  const catalogStore = useCatalogStore()

  const subtotal = (): number => cartStore.effectiveSubtotal

  // Whether this mutation moved the unlocked-tier set. Only the expanded tracker fires reward toasts
  // (see `useRewardThresholdCrossing`), so legacy-bar catalogs never report a crossing here.
  const crossedRewardTier = (beforeSubtotal: number): boolean => {
    if (!catalogStore.isExpandedRewardLadder) return false
    return (
      catalogStore.unlockedRewardTierSignature(beforeSubtotal) !==
      catalogStore.unlockedRewardTierSignature(subtotal())
    )
  }

  return {
    captureSubtotal: subtotal,
    shouldSuppressGenericCartToast: crossedRewardTier
  }
}
